import React, { useEffect, useState } from "react";
import { TrendingUp, Sparkles } from "lucide-react";
import api from "../../lib/api";

const FIELD_LABEL = {
  problem_statement: "Problem statement",
  solution: "Solution",
  product_service: "Product / Service",
  business_model: "Business model",
  revenue_model: "Revenue model",
  target_customers: "Target customers",
  market_size: "Market size",
  traction: "Traction",
  monthly_revenue: "Monthly revenue",
  annual_revenue: "Annual revenue",
  arr: "ARR",
  gross_margin: "Gross margin",
  burn_rate: "Burn rate",
  runway_months: "Runway",
  funding_type: "Funding type",
  amount_required: "Amount required",
  valuation_expectation: "Valuation",
  use_of_funds: "Use of funds",
  timeline_for_raise: "Timeline",
  competitive_advantage: "Competitive advantage",
};

/**
 * PitchHealthMeter — live 0..100 score of how well-hydrated the funding
 * profile is + top-3 highest-impact suggestions. Auto-polls when `form`
 * changes. `form` is the current funding form state (not yet saved).
 */
export default function PitchHealthMeter({ form }) {
  const [remote, setRemote] = useState(null);
  const initialFormRef = React.useRef(null);

  // Snapshot the initial server-state form so we know when the user has
  // typed anything since load; while "dirty" we prefer localScore for
  // instant feedback and skip network refetches.
  useEffect(() => {
    if (initialFormRef.current === null && form) {
      initialFormRef.current = JSON.stringify(form);
    }
  }, [form]);

  const isDirty = React.useMemo(() => {
    if (initialFormRef.current === null) return false;
    return JSON.stringify(form) !== initialFormRef.current;
  }, [form]);

  // Fetch once on mount so the meter matches the server's canonical weights.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get("/startup/pitch-health");
        if (!cancel) setRemote(res.data);
      } catch (e) { /* silent */ }
    })();
    return () => { cancel = true; };
  }, []);

  // Local instant preview from the live form so the meter feels responsive
  // before the debounced network fetch settles.
  const localScore = React.useMemo(() => {
    if (!form) return 0;
    // Same weights as the backend (kept in sync manually).
    const WEIGHTS = {
      problem_statement: 8, solution: 8, product_service: 4, business_model: 6,
      revenue_model: 6, target_customers: 5, market_size: 6, traction: 8,
      monthly_revenue: 4, annual_revenue: 5, arr: 5, gross_margin: 3,
      burn_rate: 4, runway_months: 3, funding_type: 5, amount_required: 6,
      valuation_expectation: 5, use_of_funds: 6, timeline_for_raise: 3,
      competitive_advantage: 5,
    };
    let got = 0, total = 0;
    for (const [k, w] of Object.entries(WEIGHTS)) {
      total += w;
      const v = form[k];
      const has = v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
      if (has) got += w;
    }
    return total ? Math.round(got / total * 100) : 0;
  }, [form]);

  const score = isDirty ? localScore : (remote?.score ?? localScore);
  const tips = remote?.next_tips || [];
  const tone = score >= 80 ? "emerald" : score >= 60 ? "amber" : "rose";
  const toneClass = {
    emerald: { bar: "bg-emerald-600", text: "text-emerald-700", bg: "bg-emerald-50" },
    amber:   { bar: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50" },
    rose:    { bar: "bg-rose-500",    text: "text-rose-700",    bg: "bg-rose-50" },
  }[tone];

  return (
    <div className={`rounded-lg border border-navy/10 ${toneClass.bg} p-5`} data-testid="pitch-health-meter">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp size={12} className={toneClass.text} />
            <div className="overline">Pitch Health</div>
          </div>
          <div className={`font-heading text-3xl ${toneClass.text}`} data-testid="pitch-health-score">
            {score}%
          </div>
          <div className="text-xs text-ink-muted mt-0.5">
            {score >= 80 ? "Investor-ready" : score >= 60 ? "Good — one or two gaps" : "Needs more detail"}
          </div>
        </div>
        <Sparkles size={16} className={toneClass.text} />
      </div>

      <div className="h-2 bg-white rounded-full overflow-hidden mb-4 border border-navy/5">
        <div
          className={`h-full ${toneClass.bar} transition-all duration-700 ease-out`}
          style={{ width: `${score}%` }}
        />
      </div>

      {tips.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2">
            Quick wins ({tips.length})
          </div>
          <ul className="space-y-2">
            {tips.map((t) => (
              <li key={t.field} className="flex items-start gap-2 text-xs" data-testid={`pitch-tip-${t.field}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${toneClass.bar}`} />
                <div>
                  <span className="font-heading text-ink text-sm">{FIELD_LABEL[t.field] || t.field}</span>
                  <span className="text-ink-muted"> — {t.hint}</span>
                  <span className={`ml-1 font-mono text-[9px] uppercase tracking-widest ${toneClass.text}`}>+{t.weight}pt</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
